/// <reference types="@angular/localize" />

/** The shape of a Signal Forms validation error, plus the bounds its validators carry. */
interface FieldValidationError {
  kind: string;
  message?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}

/** The part of a field state this needs: whether it has been touched, its value, and what is wrong. */
interface ValidatableFieldState {
  touched(): boolean;
  value(): unknown;
  errors(): readonly FieldValidationError[];
}

/**
 * Localized message for a field's first error, for binding to a `dm-*` control's `errormsg`.
 *
 * Bind `[errormsg]` rather than projecting a `slot="error"` block: a slotted node added
 * conditionally after the host first rendered is never relocated into the host's error row,
 * so it renders as unstyled body text outside it. A prop change re-renders the host, which
 * then owns the colour, the invalid state and the `aria-errormessage` wiring.
 *
 * `required` differs per field, so the caller passes its own localized text. Every other kind
 * the generator can emit is generic and resolved here, localized: `email`, `minLength`
 * (which also carries `minItems` for arrays), `maxLength`, `min`, `max` and `pattern`.
 *
 * The generated formgroups deliberately carry no `message` of their own — a message baked into
 * generated code cannot be localized, so it would render German in every locale. The trailing
 * fallback therefore only catches errors from outside the generator (a standard-schema or
 * native-parse error that brings its own text).
 */
export function fieldErrorMessage(field: ValidatableFieldState, requiredMessage: string): string {
  if (!field.touched()) {
    return '';
  }

  const errors = field.errors();
  if (errors.length === 0) {
    return '';
  }

  if (errors.some((error) => error.kind === 'required')) {
    return requiredMessage;
  }

  if (errors.some((error) => error.kind === 'email')) {
    return $localize`:@@forms.errors.email:Bitte eine gültige E-Mail-Adresse eingeben.`;
  }

  const tooShort = errors.find((error) => error.kind === 'minLength');
  if (tooShort?.minLength !== undefined) {
    // A `minItems` on a DTO array arrives as the same `minLength` kind, where the bound counts
    // entries rather than characters.
    if (Array.isArray(field.value())) {
      return tooShort.minLength === 1
        ? $localize`:@@forms.errors.minItemsOne:Bitte mindestens einen Eintrag wählen.`
        : $localize`:@@forms.errors.minItems:Mindestens ${tooShort.minLength}:count: Einträge wählen.`;
    }
    return $localize`:@@forms.errors.minLength:Mindestens ${tooShort.minLength}:count: Zeichen.`;
  }

  const tooLong = errors.find((error) => error.kind === 'maxLength');
  if (tooLong?.maxLength !== undefined) {
    return $localize`:@@forms.errors.maxLength:Maximal ${tooLong.maxLength}:count: Zeichen.`;
  }

  const tooSmall = errors.find((error) => error.kind === 'min');
  if (tooSmall?.min !== undefined) {
    return $localize`:@@forms.errors.min:Wert muss mindestens ${tooSmall.min}:bound: sein.`;
  }

  const tooLarge = errors.find((error) => error.kind === 'max');
  if (tooLarge?.max !== undefined) {
    return $localize`:@@forms.errors.max:Wert darf höchstens ${tooLarge.max}:bound: sein.`;
  }

  if (errors.some((error) => error.kind === 'pattern')) {
    return $localize`:@@forms.errors.pattern:Ungültiges Format.`;
  }

  return errors[0].message ?? '';
}
